// 跨时区显示工具
//
// 背景：
//   - 后端 final report 的 starttime / endtime / sleep_intention_time / intention_leave_time
//     等时间戳字段统一存 UTC 秒级 timestamp。
//   - 后端 final report 字段 `timezone` 表示与北京的偏差（北京=0，UTC-7=15，UTC+1=7）。
//     换算关系：utc_offset = 8 - timezone，即「目标当地时区」相对 UTC 偏移 = (8 - timezone) 小时。
//   - 前端图表 / 文本之前用 new Date(ts*1000).getHours() 等 API 渲染，这些 API 走浏览器
//     本地时区。北京浏览器看北京设备数据是巧合一致，看跨时区设备会错位。
//
// 解法：
//   1. 全局 displayTimezone：详情页加载报告时 setDisplayTimezone(report.timezone || 0)，
//      所有不带参的渲染函数（formatDateString / formatTime 等）默认读它。
//   2. 批量遍历多条记录的场景（Excel 导出列表），显式传 tz 参数，按各自 record.timezone。
//   3. 图表 axisLabel / tooltip 中的 d.getHours() / d.getDay() 替换为 tzDate(ts)，
//      返回一个「平移过的 Date」，对它调用 getUTCHours() / getUTCDay() 即得目标当地时间。

import { ref } from 'vue'

// 全局显示时区。0 = 北京（默认）。值同后端 timezone 字段。
export const displayTimezone = ref(0)

export function setDisplayTimezone (tz) {
  const v = Number(tz)
  displayTimezone.value = Number.isFinite(v) ? v : 0
}

export function getDisplayTimezone () {
  return displayTimezone.value || 0
}

/** 把 timezone 字段（北京=0，美西=15）换算为相对 UTC 的偏移小时数。 */
export function tzToOffsetHours (tz) {
  const v = Number(tz)
  return 8 - (Number.isFinite(v) ? v : 0)
}

/**
 * 将「ms 时间戳」按目标时区平移成一个 Date 对象，可对其调用 getUTCXxx() 系列得到目标时区当地时间。
 *
 * @param {number} tsMs 毫秒时间戳。如果传秒级会被自动放大。
 * @param {number} [tz] 后端 timezone 字段值；默认读 displayTimezone。
 * @returns {Date}
 *
 * 用法：
 *   const d = tzDate(ts * 1000, report.timezone)
 *   d.getUTCHours()  // 当地小时（不是 d.getHours()！）
 *   d.getUTCDay()    // 当地星期
 *   d.getUTCDate()   // 当地日期
 */
export function tzDate (tsMs, tz) {
  let ms = Number(tsMs)
  if (!Number.isFinite(ms)) return new Date(NaN)
  if (Math.abs(ms) < 1e12) ms = ms * 1000  // 秒 → 毫秒兜底
  const offsetH = tzToOffsetHours(tz != null ? tz : displayTimezone.value)
  return new Date(ms + offsetH * 3600 * 1000)
}

/** Date wrapper：tzDate 的简版 getter，便于在原本 d.getHours() 处直接替换。 */
export function tzGetters (tsMs, tz) {
  const d = tzDate(tsMs, tz)
  return {
    getFullYear: () => d.getUTCFullYear(),
    getMonth:    () => d.getUTCMonth(),
    getDate:     () => d.getUTCDate(),
    getDay:      () => d.getUTCDay(),
    getHours:    () => d.getUTCHours(),
    getMinutes:  () => d.getUTCMinutes(),
    getSeconds:  () => d.getUTCSeconds(),
    getMilliseconds: () => d.getUTCMilliseconds(),
    getTime:     () => d.getTime(),  // 注意：不是原始 ts，而是平移后的；通常不要直接用
  }
}
